/**
 * Evie & Bear product page: thumbnail gallery with arrows, variant selection from the option
 * radios (updates the hidden variant id used by Horizon's product-form-component, price, button
 * label, chosen-value labels and image), the size guide dialog with its age-based size finder, and
 * the sticky add-to-cart bar that appears once the main button has scrolled out of view.
 */
class EbProduct extends HTMLElement {
  #abort = new AbortController();
  /** @type {{variants: Array<{id:number, options:string[], available:boolean, price:string, compare:string|null, media:number|null}>, addLabel:string, soldOut:string}} */
  #data = { variants: [], addLabel: '', soldOut: '' };
  #index = 0;

  connectedCallback() {
    const json = this.querySelector('[data-product-json]');
    try {
      if (json?.textContent) this.#data = JSON.parse(json.textContent);
    } catch {
      // Keep the defaults: the form still adds the first variant.
    }
    const { signal } = this.#abort;
    this.addEventListener('click', (event) => this.#onClick(event), { signal });
    this.addEventListener('change', (event) => this.#onChange(event), { signal });
    this.#markUnavailable();
    this.#watchAddButton();
    this.#setupSwipe();
    const group = document.getElementById('header-group');
    if (group) {
      this.#headerObserver = new ResizeObserver(() => {
        const height = [...group.children].reduce((sum, el) => sum + (el instanceof HTMLElement ? el.offsetHeight : 0), 0);
        document.documentElement.style.setProperty('--eb-header-h', `${height}px`);
      });
      [...group.children].forEach((el) => this.#headerObserver?.observe(el));
    }
  }

  /** @type {ResizeObserver | null} */
  #headerObserver = null;

  disconnectedCallback() {
    this.#abort.abort();
    this.#abort = new AbortController();
    this.#observer?.disconnect();
    this.#observer = null;
    this.#headerObserver?.disconnect();
    this.#headerObserver = null;
  }

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** Show the sticky bar while the main add button is above the viewport. */
  #watchAddButton() {
    const sticky = this.querySelector('[data-sticky-atc]');
    const button = this.querySelector('[data-add-button]');
    if (!(sticky instanceof HTMLElement) || !(button instanceof HTMLElement)) return;
    const stickyAdd = sticky.querySelector('[data-sticky-add]');
    this.#observer = new IntersectionObserver(([entry]) => {
      if (!entry) return;
      const visible = !entry.isIntersecting && entry.boundingClientRect.top < 0;
      sticky.classList.toggle('is-visible', visible);
      sticky.setAttribute('aria-hidden', String(!visible));
      if (stickyAdd instanceof HTMLElement) stickyAdd.tabIndex = visible ? 0 : -1;
    });
    this.#observer.observe(button);
  }

  /** Phones (< 750px): the gallery is a swipe row with all photos side by side instead of one shown photo. */
  #phone = window.matchMedia('(max-width: 749px)');

  #setupSwipe() {
    const track = this.querySelector('[data-track]');
    if (!(track instanceof HTMLElement)) return;
    const { signal } = this.#abort;
    const apply = () => {
      if (this.#phone.matches) {
        this.#slides.forEach((slide) => (slide.hidden = false));
        track.scrollLeft = this.#index * track.clientWidth;
      } else {
        this.show(this.#index);
      }
    };
    this.#phone.addEventListener('change', apply, { signal });
    apply();
    let frame = 0;
    track.addEventListener(
      'scroll',
      () => {
        cancelAnimationFrame(frame);
        frame = requestAnimationFrame(() => {
          const index = Math.round(track.scrollLeft / Math.max(1, track.clientWidth));
          if (index !== this.#index) this.#mark(index);
        });
      },
      { passive: true, signal }
    );
  }

  /**
   * Marks photo `index` as current: thumbnails, phone tiles and dots, and pauses videos on the other slides.
   * @param {number} index
   */
  #mark(index) {
    const slides = this.#slides;
    this.#index = Math.max(0, Math.min(index, slides.length - 1));
    this.#thumbs.forEach((thumb, i) => thumb.setAttribute('aria-pressed', String(i === this.#index)));
    this.querySelectorAll('[data-mthumb]').forEach((tile) => {
      tile.setAttribute('aria-pressed', String(Number(/** @type {HTMLElement} */ (tile).dataset.mediaIndex) === this.#index));
    });
    this.querySelectorAll('.eb-pdp__dot').forEach((dot, i) => dot.classList.toggle('is-on', i === this.#index));
    slides.forEach((slide, i) => {
      if (i !== this.#index) slide.querySelectorAll('video').forEach((video) => video.pause());
    });
  }

  get #slides() {
    return /** @type {HTMLElement[]} */ ([...this.querySelectorAll('.eb-pdp__slide')]);
  }

  get #thumbs() {
    return /** @type {HTMLButtonElement[]} */ ([...this.querySelectorAll('.eb-pdp__thumb')]);
  }

  /** @param {number} index */
  show(index) {
    const slides = this.#slides;
    if (!slides.length) return;
    const track = this.querySelector('[data-track]');
    if (this.#phone.matches && track instanceof HTMLElement) {
      const target = (index + slides.length) % slides.length;
      track.scrollTo({ left: target * track.clientWidth, behavior: 'smooth' });
      this.#mark(target);
      return;
    }
    this.#index = (index + slides.length) % slides.length;
    slides.forEach((slide, i) => (slide.hidden = i !== this.#index));
    this.#thumbs.forEach((thumb, i) => thumb.setAttribute('aria-pressed', String(i === this.#index)));
    slides.forEach((slide, i) => {
      if (i !== this.#index) slide.querySelectorAll('video').forEach((video) => video.pause());
    });
    this.#showMore();
  }

  /**
   * Desktop photo stack under the main photo: the other photos of the colour that the main photo belongs to.
   * A colour's photos run from its variant's featured photo up to the next colour's featured photo.
   */
  #showMore() {
    const more = this.querySelector('[data-more]');
    if (!more) return;
    const total = this.#slides.length;
    const ids = this.#thumbs.map((thumb) => Number(thumb.dataset.mediaId));
    const starts = [...new Set([0, ...this.#data.variants.map((v) => (v.media ? ids.indexOf(v.media) : -1)).filter((i) => i > 0)])].sort(
      (a, b) => a - b
    );
    const start = starts.filter((i) => i <= this.#index).pop() ?? 0;
    const end = starts.find((i) => i > start) ?? total;
    const items = /** @type {HTMLElement[]} */ ([...more.querySelectorAll('.eb-pdp__more-item')]);
    const visible = items.filter((item) => {
      const i = Number(item.dataset.mediaIndex);
      const show = i >= start && i < end && i !== this.#index;
      item.hidden = !show;
      item.classList.remove('is-wide');
      return show;
    });
    if (visible.length % 2 === 1) visible[visible.length - 1].classList.add('is-wide');
  }

  /** @param {MouseEvent} event */
  #onClick(event) {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;
    const tile = target.closest('[data-mthumb]');
    if (tile instanceof HTMLElement) {
      this.show(Number(tile.dataset.mediaIndex) || 0);
      return;
    }
    const thumb = target.closest('.eb-pdp__thumb');
    if (thumb instanceof HTMLElement) {
      this.show(Number(thumb.dataset.mediaIndex) || 0);
      return;
    }
    const arrow = target.closest('[data-step]');
    if (arrow instanceof HTMLElement) {
      this.show(this.#index + Number(arrow.dataset.step));
      return;
    }
    const age = target.closest('[data-find-size]');
    if (age instanceof HTMLElement) {
      this.#findSize(age);
      return;
    }
    const pick = target.closest('[data-pick-size]');
    if (pick instanceof HTMLElement) {
      this.#pickSize(pick.dataset.size || '');
      return;
    }
    if (target.closest('[data-open-guide]')) {
      const dialog = this.querySelector('dialog');
      if (dialog instanceof HTMLDialogElement) dialog.showModal();
      return;
    }
    const dialog = target.closest('dialog');
    if (dialog instanceof HTMLDialogElement && (target === dialog || target.closest('[data-close-guide]'))) {
      dialog.close();
    }
  }

  /** @param {HTMLElement} button */
  #findSize(button) {
    const size = button.dataset.findSize || '';
    const finder = button.closest('[data-size-finder]');
    if (!finder) return;
    finder.querySelectorAll('[data-find-size]').forEach((b) => b.setAttribute('aria-pressed', String(b === button)));
    const dialog = button.closest('dialog');
    dialog?.querySelectorAll('.eb-size-table tbody tr').forEach((row) => {
      row.classList.toggle('is-match', row.getAttribute('data-size') === size);
    });
    const result = finder.querySelector('[data-finder-result]');
    const text = finder.querySelector('[data-finder-text]');
    const pick = finder.querySelector('[data-pick-size]');
    if (result instanceof HTMLElement) result.hidden = false;
    if (text instanceof HTMLElement) {
      const [before = '', after = ''] = (text.dataset.template || '').split('[size]');
      const strong = document.createElement('strong');
      strong.textContent = size;
      text.replaceChildren(before, strong, after);
    }
    if (pick instanceof HTMLElement) {
      const input = this.#sizeInput(size);
      pick.hidden = !input || input.disabled;
      pick.dataset.size = size;
      pick.textContent = (pick.dataset.template || '').replace('[size]', size);
    }
  }

  /** @param {string} size */
  #sizeInput(size) {
    const input = this.querySelector(`[data-size-option] input[value="${CSS.escape(size)}"]`);
    return input instanceof HTMLInputElement ? input : null;
  }

  /** @param {string} size */
  #pickSize(size) {
    const input = this.#sizeInput(size);
    if (!input || input.disabled) return;
    input.checked = true;
    input.dispatchEvent(new Event('change', { bubbles: true }));
    const dialog = this.querySelector('dialog');
    if (dialog instanceof HTMLDialogElement) dialog.close();
    input.focus();
  }

  #selectedOptions() {
    return /** @type {HTMLFieldSetElement[]} */ ([...this.querySelectorAll('.eb-pdp__option')]).map((fieldset) => {
      const checked = fieldset.querySelector('input:checked');
      return checked instanceof HTMLInputElement ? checked.value : '';
    });
  }

  /** @param {Event} event */
  #onChange(event) {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    const fieldset = input.closest('.eb-pdp__option');
    const label = fieldset?.querySelector('[data-option-value]');
    if (label) label.textContent = input.dataset.label || input.value;

    const selected = this.#selectedOptions();
    const variant = this.#data.variants.find((v) => v.options.every((value, i) => value === selected[i]));
    this.#markUnavailable();
    if (!variant) return;

    const idInput = this.querySelector('input[name="id"]');
    if (idInput instanceof HTMLInputElement) idInput.value = String(variant.id);
    this.dispatchEvent(new CustomEvent('eb:variant-change', { bubbles: true, detail: { variant, options: selected } }));

    for (const price of this.querySelectorAll('[data-price]')) {
      // Same format as the Liquid: bold figure with smaller raised cents, the full price for screen readers
      const match = variant.price.match(/^(.*)\.(\d{2})(.*)$/);
      price.setAttribute('aria-label', variant.price);
      price.replaceChildren();
      const shown = document.createElement('span');
      shown.setAttribute('aria-hidden', 'true');
      if (match) {
        const cents = document.createElement('span');
        cents.className = 'eb-pdp__cents';
        cents.textContent = match[2];
        shown.append(match[1], cents, match[3]);
      } else {
        shown.textContent = variant.price;
      }
      price.append(shown);
    }
    const credit = this.querySelector('[data-credit]');
    const creditAmount = credit?.querySelector('[data-credit-amount]');
    if (credit instanceof HTMLElement && creditAmount) {
      // Store credit earned on this variant: price × percent / 100, rounded to whole cents like the Flow workflow
      const cents = Number(variant.price.replace(/[^\d]/g, ''));
      const earned = Math.round((cents * (Number(credit.dataset.creditPercent) || 0)) / 100);
      creditAmount.textContent = variant.price.replace(/[\d.,]+/, (earned / 100).toFixed(2));
    }
    const lookChip = this.querySelector('[data-look-chip]');
    const lookPrice = lookChip?.querySelector('[data-look-price]');
    if (lookChip instanceof HTMLElement && lookPrice) {
      // Look price = variant price minus the look discount, in whole cents and rounded down like the discount app
      const cents = Number(variant.price.replace(/[^\d]/g, ''));
      const percent = Number(lookChip.dataset.lookPercent) || 0;
      const look = Math.floor((cents * (100 - percent)) / 100);
      const figure = `${Math.floor(look / 100)}.${String(look % 100).padStart(2, '0')}`;
      lookPrice.textContent = variant.price.replace(/[\d.,]+/, figure);
    }
    const compare = this.querySelector('[data-compare]');
    if (compare instanceof HTMLElement) {
      compare.hidden = !variant.compare;
      compare.textContent = variant.compare || '';
    }

    const button = this.querySelector('[data-add-button]');
    const buttonLabel = this.querySelector('[data-add-label]');
    if (button instanceof HTMLButtonElement) button.disabled = !variant.available;
    if (buttonLabel) {
      buttonLabel.textContent = variant.available ? `${this.#data.addLabel} · ${variant.price}` : this.#data.soldOut;
    }
    const stickyAdd = this.querySelector('[data-sticky-add]');
    const stickyLabel = this.querySelector('[data-sticky-label]');
    const stickyMeta = this.querySelector('[data-sticky-meta]');
    if (stickyAdd instanceof HTMLButtonElement) stickyAdd.disabled = !variant.available;
    if (stickyLabel) stickyLabel.textContent = buttonLabel?.textContent || '';
    const stickyShort = this.querySelector('[data-sticky-short]');
    if (stickyShort) stickyShort.textContent = variant.available ? this.#data.addLabel : this.#data.soldOut;
    const stickySize = this.querySelector('[data-sticky-size]');
    const sizeInput = this.querySelector('[data-size-option] input:checked');
    if (stickySize && sizeInput instanceof HTMLInputElement) stickySize.textContent = sizeInput.value;
    const stickyPrice = this.querySelector('[data-sticky-price]');
    if (stickyPrice) stickyPrice.textContent = variant.price;
    if (stickyMeta) {
      stickyMeta.textContent = [...this.querySelectorAll('.eb-pdp__option input:checked')]
        .map((el) => (el instanceof HTMLInputElement ? el.dataset.label || el.value : ''))
        .join(' / ');
    }

    if (variant.media) {
      const thumb = this.querySelector(`.eb-pdp__thumb[data-media-id="${variant.media}"]`);
      if (thumb instanceof HTMLElement) this.show(Number(thumb.dataset.mediaIndex) || 0);
    }

    const url = new URL(window.location.href);
    url.searchParams.set('variant', String(variant.id));
    window.history.replaceState(window.history.state, '', url);
  }

  /** Strike through values that have no available variant with the other current choices; sold-out sizes can't be picked. */
  #markUnavailable() {
    const selected = this.#selectedOptions();
    this.querySelectorAll('.eb-pdp__option').forEach((fieldset, position) => {
      fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
        if (!(input instanceof HTMLInputElement)) return;
        const options = [...selected];
        options[position] = input.value;
        const match = this.#data.variants.find((v) => v.options.every((value, i) => value === options[i]));
        const label = input.nextElementSibling;
        const unavailable = !match || !match.available;
        if (label instanceof HTMLElement) label.toggleAttribute('data-unavailable', unavailable);
        if (fieldset.hasAttribute('data-size-option')) input.disabled = unavailable && !input.checked;
      });
    });
  }
}

if (!customElements.get('eb-product')) customElements.define('eb-product', EbProduct);
